// MCP Security — Agents page.
//
// Lists OAuth clients and signed-JWT identities that have called an
// MCP endpoint on this site. Shows block rate, taint badge, risk
// weight slider; drawer renders heatmap, top tools, top endpoints,
// associated sessions.
//
// Wired 2026-10-11 to the MCP-UX routes:
//   GET /mcp/sites/:site_uid/agents
//   GET /mcp/sites/:site_uid/agents/:agent_id
//   PATCH /mcp/sites/:site_uid/agents/:agent_id/risk_weight
//   POST /mcp/sites/:site_uid/agents/:agent_id/allow
//   POST /mcp/sites/:site_uid/agents/:agent_id/block

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box, Stack, Typography, Alert, Chip, Button, Slider, LinearProgress,
} from "@mui/material";
import {
  Activity, Target, Globe, GitBranch, Shield,
} from "lucide-react";
import copy from "copy-to-clipboard";

import axios from "../../../../../utils/axios/v1/userAxios";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";

import WafEventDrawerShell from "../../detection/WafEventDrawerShell";
import SmartParseDrawerHeader from "../../detection/v3/SmartParseDrawerHeader";
import { SmartParseSectionCard } from "../../detection/v3/smartParseDrawerUi";

import tokens from "./_shared/tokens";
import MetricCard from "./_shared/MetricCard";
import useMcpQuery from "./_shared/useMcpQuery";
import {
  primaryButtonStyle,
  secondaryButtonStyle,
  dangerButtonStyle,
  BORDER_COLOR,
} from "./_shared/buttonStyles";

function riskColorForWeight(weight) {
  if (weight >= 2.0) return tokens.danger[500];
  if (weight >= 1.5) return tokens.secondary[500];
  return tokens.primaryIndigo;
}

function fmtAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!t) return "—";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} m`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  return `${Math.floor(s / 86400)} d`;
}

function AgentDrawer({ open, siteUid, agent, onClose, onWeightChange, onToggleBlock }) {
  const skip = !agent || !siteUid;
  const path = skip ? null : `mcp/sites/${siteUid}/agents/${encodeURIComponent(agent.agent_id)}`;
  const { data: resp, error, loading } = useMcpQuery(path, { skip });
  const detail = resp?.data || null;

  const [weight, setWeight] = useState(agent?.risk_weight ?? 1.0);
  const [blocked, setBlocked] = useState(!!agent?.blocked);
  useEffect(() => { if (agent) { setWeight(agent.risk_weight ?? 1.0); setBlocked(!!agent.blocked); } }, [agent]);
  const [savingWeight, setSavingWeight] = useState(false);
  const [savingBlock, setSavingBlock] = useState(false);
  const [saveErr, setSaveErr] = useState(null);

  const handleCopy = (value) => {
    if (!value || value === "—") return;
    copy(String(value));
  };

  if (!agent) return null;

  const commitWeight = async (newWeight) => {
    if (!siteUid || !agent) return;
    setSavingWeight(true);
    setSaveErr(null);
    try {
      await axios.patch(`mcp/sites/${siteUid}/agents/${encodeURIComponent(agent.agent_id)}/risk_weight`, { weight: newWeight });
      onWeightChange && onWeightChange(agent.agent_id, newWeight);
    } catch (e) {
      setSaveErr(e?.response?.data?.message || e.message || "save failed");
    } finally { setSavingWeight(false); }
  };

  const commitBlock = async (shouldBlock) => {
    if (!siteUid || !agent) return;
    setSavingBlock(true);
    setSaveErr(null);
    try {
      const url = `mcp/sites/${siteUid}/agents/${encodeURIComponent(agent.agent_id)}/${shouldBlock ? "block" : "allow"}`;
      await axios.post(url, {});
      setBlocked(shouldBlock);
      onToggleBlock && onToggleBlock(agent.agent_id, shouldBlock);
    } catch (e) {
      setSaveErr(e?.response?.data?.message || e.message || "save failed");
    } finally { setSavingBlock(false); }
  };

  // Heatmap: build a 7x24 grid out of {hour, dow, calls} rows.
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const grid = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
  const heat = detail?.heatmap || [];
  let max = 1;
  for (const r of heat) {
    // CH toDayOfWeek returns 1..7 (Mon=1 .. Sun=7)
    const d = ((Number(r.dow) || 1) - 1) % 7;
    const h = Number(r.hour) || 0;
    grid[d][h] = Number(r.calls || 0);
    if (grid[d][h] > max) max = grid[d][h];
  }
  const topToolMax = Math.max(1, ...(detail?.top_tools || []).map((t) => Number(t.calls || 0)));

  const riskColor = riskColorForWeight(weight);
  const displayName = detail?.display_name || agent.display_name || agent.agent_id;
  const oauthClient = detail?.oauth_client || agent.oauth_client || "—";

  const header = (
    <SmartParseDrawerHeader
      embedded
      title="MCP Agent"
      eventId={agent.agent_id}
      method={oauthClient}
      path={displayName}
      onCopyEventId={handleCopy}
    />
  );

  const hero = (
    <Stack spacing={1.5} sx={{ pt: 1 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={`weight ${Number(weight).toFixed(1)}×`}
          sx={{
            height: 24, fontSize: 11.5, borderRadius: "6px",
            backgroundColor: "rgba(99,113,247,0.10)",
            color: riskColor,
            border: `1px solid ${BORDER_COLOR}`,
            fontFamily: tokens.fontMono,
          }}
        />
        {blocked && (
          <Chip
            size="small"
            label="blocked"
            sx={{
              height: 24, fontSize: 11.5, borderRadius: "6px",
              backgroundColor: tokens.danger.bg,
              color: tokens.danger[600],
              border: `1px solid ${BORDER_COLOR}`,
            }}
          />
        )}
      </Stack>
    </Stack>
  );

  const footerActions = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Button onClick={onClose} sx={secondaryButtonStyle}>Close</Button>
      <Button
        sx={primaryButtonStyle}
        disabled={savingBlock || !blocked}
        onClick={() => commitBlock(false)}
      >
        Add to allow-list
      </Button>
      <Button
        sx={dangerButtonStyle}
        disabled={savingBlock || blocked}
        onClick={() => commitBlock(true)}
      >
        Add to block-list
      </Button>
    </Stack>
  );

  return (
    <WafEventDrawerShell
      open={!!open}
      onClose={onClose}
      title="MCP Agent"
      headerVariant="hero"
      headerContent={header}
      heroContent={hero}
      loading={loading}
      error={error || null}
      footerActions={footerActions}
    >
      {saveErr && <Alert severity="warning">{saveErr}</Alert>}

      <SmartParseSectionCard icon={Activity} title="Activity · hour × day (24h)">
        <Stack spacing={0.25}>
          {days.map((d, di) => (
            <Box key={d} sx={{ display: "grid", gridTemplateColumns: "32px repeat(24, minmax(0, 1fr))", gap: "2px", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: tokens.neutral[700] }}>{d}</span>
              {grid[di].map((v, i) => {
                const r = v / max;
                const bg = r > 0.75 ? tokens.primaryIndigo
                  : r > 0.5 ? "rgba(99,113,247,0.6)"
                  : r > 0.25 ? "rgba(99,113,247,0.3)" : tokens.neutral[100];
                return <Box key={i} title={`${v} calls`} sx={{ height: 14, borderRadius: "2px", background: bg }} />;
              })}
            </Box>
          ))}
        </Stack>
      </SmartParseSectionCard>

      <SmartParseSectionCard icon={Target} title="Top tools called (7d)">
        {(detail?.top_tools || []).map((t) => {
          const w = `${Math.round((Number(t.calls || 0) * 100) / topToolMax)}%`;
          return (
            <Box key={t.tool_name} sx={{ display: "grid", gridTemplateColumns: "1fr 60px", gap: 1, alignItems: "center", fontSize: 12, mb: 0.75 }}>
              <Box>
                <code style={{ fontFamily: tokens.fontMono }}>{t.tool_name}</code>
                <Box sx={{ height: 4, background: tokens.neutral[100], borderRadius: 999, mt: 0.5 }}>
                  <Box sx={{ height: "100%", borderRadius: 999, background: tokens.primaryIndigo, width: w }} />
                </Box>
              </Box>
              <Typography sx={{ textAlign: "right", fontFamily: tokens.fontMono, fontSize: 12 }}>{t.calls}</Typography>
            </Box>
          );
        })}
        {(detail?.top_tools || []).length === 0 && !loading && (
          <Typography sx={{ fontSize: 12, color: tokens.neutral[600] }}>No calls in the last 7 days.</Typography>
        )}
      </SmartParseSectionCard>

      <SmartParseSectionCard icon={Globe} title="Top endpoints (7d)">
        {(detail?.top_endpoints || []).map((e, i, arr) => (
          <Stack key={e.endpoint_id} direction="row" justifyContent="space-between" spacing={1}
            sx={{ fontSize: 12, py: 0.5, borderBottom: i < arr.length - 1 ? `1px solid ${BORDER_COLOR}` : "none" }}>
            <code style={{ fontFamily: tokens.fontMono, overflow: "hidden", textOverflow: "ellipsis" }}>{e.endpoint_id.slice(0, 24)}</code>
            <span style={{ fontFamily: tokens.fontMono }}>{e.calls}</span>
          </Stack>
        ))}
        {(detail?.top_endpoints || []).length === 0 && !loading && (
          <Typography sx={{ fontSize: 12, color: tokens.neutral[600] }}>—</Typography>
        )}
      </SmartParseSectionCard>

      <SmartParseSectionCard icon={GitBranch} title="Associated sessions">
        <Box sx={{ border: `1px solid ${BORDER_COLOR}`, borderRadius: "8px", overflow: "hidden" }}>
          {(detail?.sessions || []).length === 0 && !loading && (
            <Typography sx={{ p: 1.5, fontSize: 12, color: tokens.neutral[700] }}>No sessions in the last 7 days.</Typography>
          )}
          {(detail?.sessions || []).map((x, i, arr) => (
            <Stack key={x.session_fp} direction="row" alignItems="center" spacing={1}
              sx={{ p: 1, fontSize: 12, borderBottom: i < arr.length - 1 ? `1px solid ${BORDER_COLOR}` : "none" }}>
              <span style={{ fontFamily: tokens.fontMono, color: tokens.primary[300], width: 90 }}>{x.session_fp.slice(0, 10)}</span>
              <Typography sx={{ flex: 1, color: tokens.neutral[700], fontSize: 12 }}>{x.calls} calls · {fmtAgo(x.last_seen)} ago</Typography>
            </Stack>
          ))}
        </Box>
      </SmartParseSectionCard>

      <SmartParseSectionCard icon={Shield} title="Risk weight">
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Slider
            value={Number(weight) || 1}
            min={0.5}
            max={2}
            step={0.1}
            onChange={(_, v) => setWeight(v)}
            onChangeCommitted={(_, v) => commitWeight(v)}
            sx={{ flex: 1, color: tokens.primaryIndigo }}
            disabled={savingWeight}
          />
          <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 14, fontWeight: 500, width: 44, textAlign: "right" }}>
            {Number(weight).toFixed(1)}×
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: tokens.neutral[700], mt: 0.5 }}>
          Multiplies this agent's contribution to the per-session risk score.
        </Typography>
      </SmartParseSectionCard>
    </WafEventDrawerShell>
  );
}

export default function McpAgents() {
  const { effectiveSiteUid } = useGlobalSite();
  const [drawerAgent, setDrawerAgent] = useState(null);

  const path = effectiveSiteUid ? `mcp/sites/${effectiveSiteUid}/agents?limit=200` : null;
  const { data: resp, error, loading, refetch } = useMcpQuery(path, { skip: !path });
  const agents = resp?.data || [];

  const metrics = useMemo(() => [
    { label: "Agents seen", value: String(agents.length), sub: "last 7 days" },
    { label: "High risk", value: String(agents.filter((a) => (a.risk_weight ?? 1) >= 2.0).length), sub: "weight >= 2.0", fg: tokens.danger[600] },
    { label: "Blocked", value: String(agents.filter((a) => a.blocked).length), sub: "operator-blocked", fg: tokens.secondary[500] },
    { label: "Blocked / 24h", value: String(agents.reduce((s, a) => s + Number(a.blocks || 0), 0)), sub: "scope + rate + injection" },
  ], [agents]);

  return (
    <>
      <Helmet title="MCP Security — Agents" />
      <Stack spacing={3} sx={{ p: 6 }}>
        <V2PageHeader
          title="MCP Security — Agents"
          description="OAuth clients and signed-JWT identities talking to your MCP endpoints."
        />
        <SiteScopeGate mode="any">
          {() => (
            <>
              {!effectiveSiteUid && <Alert severity="info">Select a site from the header to see agents.</Alert>}
              {error && <Alert severity="error">{error}</Alert>}

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.5 }}>
                {metrics.map((m) => (
                  <MetricCard key={m.label} label={m.label} value={m.value} sub={m.sub} />
                ))}
              </Box>

              <Box sx={{ background: "#fff", border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden" }}>
                <Box sx={{ p: 1.75, borderBottom: `1px solid ${tokens.neutral[200]}` }}>
                  <Typography sx={{ fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: tokens.neutral[1000] }}>Agents</Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
                    OAuth clients and signed JWT identities talking to your MCP endpoints.
                  </Typography>
                </Box>
                {loading && <LinearProgress />}
                <Box sx={{ overflowX: "auto" }}>
                  <Box sx={{ minWidth: 1100 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "110px minmax(160px,1.2fr) 140px 80px 80px 80px 90px 70px 140px",
                        gap: "0 12px",
                        px: 2, py: 1.25,
                        background: tokens.neutral[50],
                        borderBottom: `1px solid ${tokens.neutral[300]}`,
                        fontSize: 11, fontWeight: 500, color: tokens.neutral[700],
                        textTransform: "uppercase", letterSpacing: "0.04em",
                      }}
                    >
                      <span>agent_id</span><span>Display name</span><span>OAuth client</span>
                      <span>First seen</span><span>Last seen</span><span>Calls</span>
                      <span>Block rate</span><span>Blocked</span><span>Risk</span>
                    </Box>
                    {agents.length === 0 && !loading && (
                      <Box sx={{ p: 3, textAlign: "center", color: tokens.neutral[700], fontSize: 13 }}>
                        No agents observed in the last 7 days.
                      </Box>
                    )}
                    {agents.map((a) => {
                      const rate = Math.min(100, Math.round((a.risk_weight ?? 1) * 50));
                      const brFg = a.block_rate > 0.1 ? "#8E1F1F" : a.block_rate > 0.01 ? "#8A6410" : "#1E6B3F";
                      return (
                        <Box
                          key={a.agent_id}
                          onClick={() => setDrawerAgent(a)}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "110px minmax(160px,1.2fr) 140px 80px 80px 80px 90px 70px 140px",
                            gap: "0 12px",
                            px: 2, height: 44, alignItems: "center",
                            borderBottom: `1px solid ${tokens.neutral[150]}`,
                            fontSize: 13, cursor: "pointer",
                            background: a.blocked ? "rgba(254, 215, 215, 0.4)" : "#fff",
                            "&:hover": { background: tokens.neutral[50] },
                          }}
                        >
                          <span style={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.primary[300] }}>
                            {a.agent_id.length > 12 ? a.agent_id.slice(0, 12) + "…" : a.agent_id}
                          </span>
                          <span style={{ fontWeight: 500 }}>{a.display_name || "—"}</span>
                          <span style={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.neutral[800] }}>{a.oauth_client || "—"}</span>
                          <span style={{ color: tokens.neutral[700] }}>{fmtAgo(a.first_seen)}</span>
                          <span style={{ color: tokens.neutral[700] }}>{fmtAgo(a.last_seen)}</span>
                          <span style={{ fontFamily: tokens.fontMono }}>{Number(a.calls || 0).toLocaleString()}</span>
                          <span style={{ fontFamily: tokens.fontMono, color: brFg }}>{(Number(a.block_rate || 0) * 100).toFixed(1)}%</span>
                          <span style={{ fontFamily: tokens.fontMono }}>{a.blocked ? "yes" : "no"}</span>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={{ flex: 1, height: 6, borderRadius: 999, background: tokens.neutral[100] }}>
                              <Box sx={{ height: "100%", borderRadius: 999, width: `${rate}%`, background: riskColorForWeight(a.risk_weight ?? 1) }} />
                            </Box>
                            <Typography sx={{ fontFamily: tokens.fontMono, width: 32, textAlign: "right", fontSize: 12 }}>
                              {Number(a.risk_weight ?? 1).toFixed(1)}×
                            </Typography>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </SiteScopeGate>
      </Stack>

      <AgentDrawer
        open={!!drawerAgent}
        siteUid={effectiveSiteUid}
        agent={drawerAgent}
        onClose={() => setDrawerAgent(null)}
        onWeightChange={() => refetch()}
        onToggleBlock={() => refetch()}
      />
    </>
  );
}
